import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  SavingsMovementDto,
  SavingsMovementRequestDto,
} from "@/features/savings/api/dto";

/**
 * Verse `amountMinor` depuis un portefeuille principal du titulaire vers le
 * coffre (`POST /SavingsVaults/{id}/deposit`) — autorisé même pendant un
 * blocage. Produit `SAVINGS_DEPOSIT`, gratuit par défaut. COMPLETED cas
 * nominal ; PENDING si wallet-service est en attente (rejouer la même clé
 * relit le statut sans déplacer l'argent deux fois).
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps). 422 : coffre clos, solde insuffisant, portefeuille inactif.
 */
export const useDepositSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "deposit", vaultId],
    mutationFn: (body: SavingsMovementRequestDto) =>
      mutateIdempotent<SavingsMovementDto>({
        scopeId: `savings.savingsVaults.deposit.${vaultId}`,
        url: savingsEndpoints.savingsVaults.deposit(vaultId),
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDepositSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
